import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  makeStyles,
} from "@material-ui/core";
import { Close, FileCopy, OpenInNew } from "@material-ui/icons";

const useStyles = makeStyles(() => ({
  dialogPaper: {
    maxWidth: "90vw",
    width: "90vw",
    maxHeight: "85vh",
  },
  titleBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  xmlContent: {
    fontFamily: "monospace",
    fontSize: "0.8rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    backgroundColor: "#f5f5f5",
    padding: "16px",
    borderRadius: "4px",
    overflow: "auto",
    maxHeight: "65vh",
    lineHeight: 1.4,
  },
  actions: {
    justifyContent: "flex-start",
    gap: "8px",
    padding: "8px 24px 16px",
  },
}));

interface XmlViewerModalProps {
  open: boolean;
  onClose: () => void;
  xml: string | null;
  isLoading: boolean;
  blobName: string;
}

export default function XmlViewerModal({ open, onClose, xml, isLoading, blobName }: XmlViewerModalProps) {
  const classes = useStyles();
  const [copySnackbar, setCopySnackbar] = useState(false);

  const handleCopy = () => {
    if (xml) {
      navigator.clipboard.writeText(xml);
      setCopySnackbar(true);
    }
  };

  const handleOpenNewTab = () => {
    if (xml) {
      const blob = new Blob([xml], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }}>
        <DialogTitle>
          <div className={classes.titleBar}>
            <span>{blobName || "XML Viewer"}</span>
            <IconButton size="small" onClick={onClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          {isLoading ? <CircularProgress /> : <pre className={classes.xmlContent}>{xml || "No content"}</pre>}
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button
            startIcon={<FileCopy />}
            onClick={handleCopy}
            variant="outlined"
            size="small"
            disabled={!xml}
          >
            Copy
          </Button>
          <Button
            startIcon={<OpenInNew />}
            onClick={handleOpenNewTab}
            variant="outlined"
            size="small"
            disabled={!xml}
          >
            Open in New Tab
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="Copied to clipboard"
      />
    </>
  );
}
